import { useState, useCallback, useMemo } from 'react';
import { z } from 'zod';

export interface ErroValidacao {
  campo: string;
  mensagem: string;
  tipo: 'erro' | 'aviso' | 'info';
  sugestao?: string;
}

export interface SugestaoValidacao {
  campo: string;
  mensagem: string;
  tipo: 'sugestao' | 'dica' | 'otimizacao';
  acao?: string;
}

export const useValidacaoProgressiva = () => {
  const [erros, setErros] = useState<Record<string, ErroValidacao>>({});
  const [sugestoes, setSugestoes] = useState<Record<string, SugestaoValidacao>>({});
  const [validando, setValidando] = useState<Record<string, boolean>>({});

  // Validar campo individual
  const validarCampo = useCallback(async (
    campo: string, 
    valor: any, 
    schema?: z.ZodSchema,
    validacoesCustomizadas?: Record<string, (valor: any) => Promise<ErroValidacao | null>>
  ): Promise<boolean> => {
    setValidando(prev => ({ ...prev, [campo]: true }));

    try {
      // Limpar erros anteriores
      setErros(prev => {
        const { [campo]: _, ...rest } = prev;
        return rest;
      });

      // Validação básica de campo obrigatório
      if (valor === null || valor === undefined || valor === '') {
        if (schema && schema._def.typeName === 'ZodString' && schema._def.checks?.some((c: any) => c.kind === 'min')) {
          setErros(prev => ({
            ...prev,
            [campo]: {
              campo,
              mensagem: 'Este campo é obrigatório',
              tipo: 'erro'
            }
          }));
          return false;
        }
      }

      // Validação com schema Zod
      if (schema && valor !== null && valor !== undefined && valor !== '') {
        try {
          schema.parse(valor);
        } catch (error) {
          if (error instanceof z.ZodError) {
            const erroZod = error.errors[0];
            setErros(prev => ({
              ...prev,
              [campo]: {
                campo,
                mensagem: erroZod.message,
                tipo: 'erro',
                sugestao: gerarSugestao(erroZod)
              }
            }));
            return false;
          }
        }
      }

      // Validações customizadas
      if (validacoesCustomizadas?.[campo]) {
        const erroCustomizado = await validacoesCustomizadas[campo](valor);
        if (erroCustomizado) {
          setErros(prev => ({
            ...prev,
            [campo]: erroCustomizado
          }));
          return false;
        }
      }

      // Gerar sugestões se o campo for válido
      const sugestao = await gerarSugestaoInteligente(campo, valor);
      if (sugestao) {
        setSugestoes(prev => ({
          ...prev,
          [campo]: sugestao
        }));
      }

      return true;
    } catch (error) {
      console.error(`Erro ao validar campo ${campo}:`, error);
      setErros(prev => ({
        ...prev,
        [campo]: {
          campo,
          mensagem: 'Erro interno de validação',
          tipo: 'erro'
        }
      }));
      return false;
    } finally {
      setValidando(prev => ({ ...prev, [campo]: false }));
    }
  }, []);

  // Validar formulário completo
  const validarFormulario = useCallback(async (
    dados: Record<string, any>,
    schema: z.ZodSchema
  ): Promise<{ valido: boolean; erros: ErroValidacao[] }> => {
    try {
      schema.parse(dados);
      return { valido: true, erros: [] };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errosFormulario = error.errors.map(erro => ({
          campo: erro.path.join('.'),
          mensagem: erro.message,
          tipo: 'erro' as const,
          sugestao: gerarSugestao(erro)
        }));

        // Atualizar estado de erros
        const errosAtualizados: Record<string, ErroValidacao> = {};
        errosFormulario.forEach(erro => {
          errosAtualizados[erro.campo] = erro;
        });
        setErros(errosAtualizados);

        return { valido: false, erros: errosFormulario };
      }
      return { valido: false, erros: [] };
    }
  }, []);

  // Gerar sugestão baseada no erro Zod
  const gerarSugestao = (erro: z.ZodIssue): string => {
    switch (erro.code) {
      case 'too_small':
        if (erro.type === 'string') {
          return `Digite pelo menos ${erro.minimum} caracteres`;
        }
        if (erro.type === 'number') {
          return `O valor deve ser maior que ${erro.minimum}`;
        }
        break;
      case 'too_big':
        if (erro.type === 'string') {
          return `Digite no máximo ${erro.maximum} caracteres`;
        }
        if (erro.type === 'number') {
          return `O valor deve ser menor que ${erro.maximum}`;
        }
        break;
      case 'invalid_string':
        if (erro.validation === 'email') {
          return 'Digite um email válido (ex: usuario@exemplo.com)';
        }
        if (erro.validation === 'url') {
          return 'Digite uma URL válida (ex: https://exemplo.com)';
        }
        break;
      case 'custom':
        return erro.message;
      default:
        return erro.message;
    }
    return erro.message;
  };

  // Gerar sugestão inteligente baseada no campo e valor
  const gerarSugestaoInteligente = async (
    campo: string, 
    valor: any
  ): Promise<SugestaoValidacao | null> => {
    // Sugestões baseadas no campo
    switch (campo) {
      case 'cpf':
        if (typeof valor === 'string' && valor.length === 11) {
          return {
            campo,
            mensagem: 'CPF válido! Buscando dados do cliente...',
            tipo: 'info',
            acao: 'buscar_cliente'
          };
        }
        break;

      case 'email':
        if (typeof valor === 'string' && valor.includes('@')) {
          return {
            campo,
            mensagem: 'Email válido! Enviaremos notificações para este endereço.',
            tipo: 'dica'
          };
        }
        break;

      case 'valorParcela':
        if (typeof valor === 'number' && valor > 0) {
          if (valor < 100) {
            return {
              campo,
              mensagem: 'Valor baixo. Considere grupos com taxa administrativa menor.',
              tipo: 'sugestao',
              acao: 'sugerir_grupos'
            };
          } else if (valor > 5000) {
            return {
              campo,
              mensagem: 'Valor alto. Considere grupos premium com melhor liquidez.',
              tipo: 'sugestao',
              acao: 'sugerir_grupos_premium'
            };
          }
        }
        break;

      case 'telefone':
        if (typeof valor === 'string' && valor.length >= 10) {
          return {
            campo,
            mensagem: 'Telefone válido! Usaremos para contato direto.',
            tipo: 'info'
          };
        }
        break;

      default:
        return null;
    }

    return null;
  };

  // Limpar erro específico
  const limparErro = useCallback((campo: string) => {
    setErros(prev => {
      const { [campo]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  // Limpar sugestão específica
  const limparSugestao = useCallback((campo: string) => {
    setSugestoes(prev => {
      const { [campo]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  // Limpar todos os erros e sugestões
  const limparTudo = useCallback(() => {
    setErros({});
    setSugestoes({});
    setValidando({});
  }, []);

  // Verificar se há erros
  const temErros = useMemo(() => Object.keys(erros).length > 0, [erros]);
  
  // Verificar se há sugestões
  const temSugestoes = useMemo(() => Object.keys(sugestoes).length > 0, [sugestoes]);

  // Verificar se está validando algum campo
  const estaValidando = useMemo(() => Object.values(validando).some(v => v), [validando]);

  // Obter erros por campo
  const obterErro = useCallback((campo: string): ErroValidacao | undefined => {
    return erros[campo];
  }, [erros]);

  // Obter sugestão por campo
  const obterSugestao = useCallback((campo: string): SugestaoValidacao | undefined => {
    return sugestoes[campo];
  }, [sugestoes]);

  // Obter status do campo
  const obterStatusCampo = useCallback((campo: string): 'validando' | 'erro' | 'sucesso' | 'neutro' => {
    if (validando[campo]) return 'validando';
    if (erros[campo]) return 'erro';
    if (sugestoes[campo]?.tipo === 'info') return 'sucesso';
    return 'neutro';
  }, [validando, erros, sugestoes]);

  return {
    // Estado
    erros,
    sugestoes,
    validando,
    
    // Status
    temErros,
    temSugestoes,
    estaValidando,
    
    // Funções
    validarCampo,
    validarFormulario,
    limparErro,
    limparSugestao,
    limparTudo,
    obterErro,
    obterSugestao,
    obterStatusCampo
  };
};